// Planilla de árbitros: PDF con los partidos de una jornada, una planilla por cada
// zona que juega ese día. Cada planilla lleva una tabla por cancha, con un
// casillero en blanco al lado de cada equipo para anotar el resultado a mano.
//
// Los partidos con walk-over van tachados y, en una columna extra al final de la
// grilla, llevan en negrita el rótulo "WO" (o "DWO" si lo dieron los dos equipos).
// Esa columna solo aparece si la jornada tiene algún WO. El casillero de resultado igual queda en blanco: el 3 a 0 no se anota
// a mano, sale de la carga de resultados.
//
// Solo entran los partidos que ya tienen cancha Y horario: uno sin programar no
// se puede arbitrar, así que queda afuera de la grilla (ver `estaProgramado`).
//
// Van 2 canchas por hoja A4 (lo normal es que una zona tenga una o dos, así que
// cada zona entra en una sola página) y cada zona arranca en hoja nueva. Con más
// canchas se agregan hojas. El alto de las filas y la tipografía se calculan antes
// de dibujar (ver `elegirLayout`) para que las canchas de cada hoja entren
// completas; la escala es única para todo el PDF, así todas las zonas se ven
// iguales.
//
// jspdf/jspdf-autotable se cargan con import() dinámico para que no entren en el
// bundle principal de la PWA: solo se bajan cuando se toca el botón.

import { fechaCalendario } from "./fechas";
import { agruparPorCancha, claveDeCancha, enMinutos, estaProgramado } from "./horarios";
import { esWO, esWODoble } from "./resultado";

// jsPDF mide las fuentes en puntos y la hoja en milímetros.
const MM_POR_PUNTO = 0.3528;
const INTERLINEADO = 1.15;

// La duración del partido no se carga en ningún lado: se deduce del espaciado de
// los horarios de la grilla, que lleva 5 minutos extra para la entrada y salida de
// los equipos (partidos cada 20 minutos => partidos de 15). Se toma el espaciado
// más repetido dentro de cada cancha. Devuelve null si no se puede deducir.
export function duracionDePartido(matches = []) {
  const porCancha = {};
  matches.forEach((m) => {
    const minutos = enMinutos(m.hora);
    if (minutos == null) return;
    const clave = claveDeCancha(m);
    porCancha[clave] = porCancha[clave] || [];
    porCancha[clave].push(minutos);
  });

  const espaciados = [];
  Object.values(porCancha).forEach((horarios) => {
    const ordenados = [...new Set(horarios)].sort((a, b) => a - b);
    for (let i = 1; i < ordenados.length; i++) espaciados.push(ordenados[i] - ordenados[i - 1]);
  });
  if (espaciados.length === 0) return null;

  const conteo = {};
  espaciados.forEach((e) => (conteo[e] = (conteo[e] || 0) + 1));
  const masRepetido = Number(
    Object.keys(conteo).sort((a, b) => conteo[b] - conteo[a] || Number(a) - Number(b))[0]
  );

  const duracion = masRepetido - 5;
  return duracion > 0 ? duracion : null;
}

const CANCHAS_POR_HOJA = 2;

// Las canchas se reparten en hojas de a CANCHAS_POR_HOJA, en orden.
function repartirEnHojas(grupos) {
  const hojas = [];
  for (let i = 0; i < grupos.length; i += CANCHAS_POR_HOJA)
    hojas.push(grupos.slice(i, i + CANCHAS_POR_HOJA));
  return hojas.length ? hojas : [[]];
}

// Rótulo del walk-over en la grilla: "DWO" cuando lo dieron los dos equipos.
const rotuloWO = (match) => (esWODoble(match) ? "DWO" : esWO(match) ? "WO" : "");

// Las fechas de la jornada que tienen algo para arbitrar, cada una con sus
// partidos ya filtrados en `partidos`. Lo usan la planilla y el modal que la
// ofrece, para que lo que se anuncia sea lo que termina saliendo en el PDF.
export function fechasConPartidosProgramados(fechas = []) {
  return fechas
    .map((f) => ({ ...f, partidos: ((f.matchDate && f.matchDate.matches) || []).filter(estaProgramado) }))
    .filter((f) => f.partidos.length > 0);
}

const limpiarNombreArchivo = (texto) => texto.replace(/[\\/:*?"<>|]/g, "-").replace(/\s+/g, " ").trim();

// fechas: [{ zone, matchDate, numero }] — las fechas de la jornada, una por zona,
// tal como las devuelve `jornadasDeRueda`. numero / dia / torneoNombre / torneoAno
// son de la jornada y solo se usan para el nombre del archivo (el encabezado de
// cada hoja lo arma cada zona con su propio número de fecha).
// teams: equipos del torneo.
export async function generarPlanillaArbitros({
  fechas = [],
  numero,
  dia,
  torneoNombre,
  torneoAno,
  teams = [],
}) {
  // Una zona sin partidos programados no tiene nada que arbitrar.
  const conPartidos = fechasConPartidosProgramados(fechas);
  if (conPartidos.length === 0) return;

  const nombreDeEquipo = (teamId) => {
    const t = teams.find((x) => x.id === teamId);
    return t ? t.name : "—";
  };

  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const anchoPagina = doc.internal.pageSize.getWidth();
  const altoPagina = doc.internal.pageSize.getHeight();
  const margen = 14;
  const anchoUtil = anchoPagina - margen * 2;
  const topTablas = 34; // debajo del encabezado general
  const altoDisponible = altoPagina - topTablas - margen;

  // Una planilla por zona, cada una con sus hojas de a 2 canchas.
  const planillas = conPartidos.map(({ zone, matchDate, numero, partidos }) => ({
    titulo: (zone.name || "Zona").toUpperCase(),
    duracion: duracionDePartido(partidos),
    subtitulo: [numero ? "Fecha " + numero : "", fechaCalendario(matchDate)].filter(Boolean).join(" · "),
    hojas: repartirEnHojas(agruparPorCancha(partidos)),
  }));

  const hojas = planillas.flatMap((p) => p.hojas);
  const partidosEnJuego = hojas.flat().flatMap((g) => g.partidos);
  const nombresEnJuego = partidosEnJuego.flatMap((m) => [
    nombreDeEquipo(m.homeTeamId),
    nombreDeEquipo(m.awayTeamId),
  ]);
  // La columna del WO solo se agrega si hay alguno: si no, la grilla queda como
  // siempre y los nombres de equipo aprovechan todo el ancho.
  const hayWO = partidosEnJuego.some(esWO);
  const COLUMNA_WO = 5;

  const anchoDeTexto = (texto, fontSize, estilo = "normal") => {
    doc.setFont("helvetica", estilo);
    doc.setFontSize(fontSize);
    return doc.getTextWidth(texto);
  };

  // Ancho del nombre de equipo más largo, para elegir un cuerpo de letra en el que
  // ningún nombre se parta en dos renglones (si se parte, la fila crece y la
  // planilla se va de página).
  const anchoNombreMasLargo = (fontSize) =>
    nombresEnJuego.reduce((max, n) => Math.max(max, anchoDeTexto(n, fontSize)), 0);

  // Medidas concretas de un layout candidato: `escala` achica el alto de las filas
  // y la tipografía hasta que las canchas de cada hoja entren completas.
  const medirLayout = (escala) => {
    const anchoHora = 18;
    const anchoRdo = 22;
    const anchoWO = hayWO ? 14 : 0; // entra "DWO" en negrita
    const anchoEquipo = (anchoUtil - anchoHora - anchoRdo * 2 - anchoWO) / 2;
    const padding = Math.max(0.7, 1.8 * escala);
    const anchoTextoEquipo = anchoEquipo - padding * 2;

    // Cuerpo de letra: el ideal según la escala, achicado hasta que entre el
    // nombre más largo (con un piso de 5pt, abajo de eso no se lee).
    let fontBody = Math.min(10, Math.max(5, 10 * escala));
    while (fontBody > 5 && anchoNombreMasLargo(fontBody) > anchoTextoEquipo) fontBody -= 0.25;
    const fontHead = Math.max(5, fontBody - 1);

    const renglones = Math.max(1, Math.ceil(anchoNombreMasLargo(fontBody) / anchoTextoEquipo));
    const altoFila = Math.max(9 * escala, renglones * fontBody * MM_POR_PUNTO * INTERLINEADO + padding * 2);
    const altoCabecera = Math.max(7 * escala, fontHead * MM_POR_PUNTO * INTERLINEADO + padding * 2);
    const fontTitulo = Math.max(7, Math.min(12, 12 * escala));
    const altoTitulo = fontTitulo * MM_POR_PUNTO + 2.5 * escala;
    const sepGrupos = Math.max(3, 9 * escala);

    const altoDeHoja = (gs) =>
      gs.reduce((alto, g) => alto + altoTitulo + altoCabecera + g.partidos.length * altoFila, 0) +
      Math.max(0, gs.length - 1) * sepGrupos;
    const altoNecesario = Math.max(0, ...hojas.map(altoDeHoja));

    return {
      escala,
      anchoHora,
      anchoRdo,
      anchoWO,
      anchoEquipo,
      padding,
      fontBody,
      fontHead,
      fontTitulo,
      altoFila,
      altoCabecera,
      altoTitulo,
      sepGrupos,
      entra: altoNecesario <= altoDisponible,
    };
  };

  // La escala es única para todo el PDF (todas las hojas se ven iguales): se busca
  // la más grande con la que entre la hoja más cargada.
  const elegirLayout = () => {
    for (let escala = 1; escala >= 0.4; escala -= 0.02) {
      const layout = medirLayout(escala);
      if (layout.entra) return layout;
    }
    // Ni al mínimo entra (una cancha con muchísimos partidos): se usa lo más chico
    // y jspdf agrega la página que haga falta.
    return medirLayout(0.4);
  };

  const layout = elegirLayout();

  // Encabezado general: zona a la izquierda, duración de los partidos a la derecha.
  // Cambia con cada zona, así que se guarda el de la que se está dibujando.
  let encabezado = planillas[0];
  const paginasConEncabezado = new Set();
  const dibujarEncabezado = () => {
    const { titulo, duracion, subtitulo } = encabezado;
    const pagina = doc.internal.getCurrentPageInfo().pageNumber;
    if (paginasConEncabezado.has(pagina)) return;
    paginasConEncabezado.add(pagina);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(titulo, margen, 18);

    if (duracion) {
      doc.setFontSize(12);
      doc.text("Partidos de " + duracion + " minutos", anchoPagina - margen, 18, { align: "right" });
    }

    if (subtitulo) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(110, 110, 110);
      doc.text(subtitulo, margen, 24);
      doc.setTextColor(0, 0, 0);
    }

    doc.setDrawColor(180, 180, 180);
    doc.line(margen, 27, anchoPagina - margen, 27);
  };

  let primeraHoja = true;
  planillas.forEach((planilla) => {
    encabezado = planilla;
    planilla.hojas.forEach((gruposDeHoja) => {
      if (!primeraHoja) doc.addPage();
      primeraHoja = false;
      dibujarEncabezado();
      let y = topTablas;

      gruposDeHoja.forEach((grupo) => {
        const altoDelGrupo = layout.altoTitulo + layout.altoCabecera + grupo.partidos.length * layout.altoFila;
        // Red de seguridad para el caso en que ni la escala mínima alcanzó.
        if (y > topTablas && y + altoDelGrupo > altoPagina - margen) {
          doc.addPage();
          dibujarEncabezado();
          y = topTablas;
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(layout.fontTitulo);
        doc.setTextColor(0, 0, 0);
        doc.text(grupo.titulo, margen, y + layout.fontTitulo * MM_POR_PUNTO);

        autoTable(doc, {
          startY: y + layout.altoTitulo,
          margin: { top: topTablas, bottom: margen, left: margen, right: margen },
          head: [hayWO ? ["", "EQUIPO", "RDO", "EQUIPO", "RDO", ""] : ["", "EQUIPO", "RDO", "EQUIPO", "RDO"]],
          body: grupo.partidos.map((m) => {
            const fila = [m.hora || "", nombreDeEquipo(m.homeTeamId), "", nombreDeEquipo(m.awayTeamId), ""];
            if (hayWO) fila.push(rotuloWO(m));
            return fila;
          }),
          theme: "grid",
          styles: {
            font: "helvetica",
            fontSize: layout.fontBody,
            cellPadding: layout.padding,
            minCellHeight: layout.altoFila,
            valign: "middle",
            lineColor: [90, 90, 90],
            lineWidth: 0.2,
            textColor: [0, 0, 0],
          },
          headStyles: {
            fontStyle: "bold",
            fontSize: layout.fontHead,
            halign: "center",
            minCellHeight: layout.altoCabecera,
            fillColor: [225, 225, 225],
            textColor: [0, 0, 0],
          },
          columnStyles: {
            0: { cellWidth: layout.anchoHora, halign: "center", fontStyle: "bold" },
            1: { cellWidth: layout.anchoEquipo },
            2: { cellWidth: layout.anchoRdo },
            3: { cellWidth: layout.anchoEquipo },
            4: { cellWidth: layout.anchoRdo },
            ...(hayWO
              ? { [COLUMNA_WO]: { cellWidth: layout.anchoWO, halign: "center", fontStyle: "bold" } }
              : {}),
          },
          // El partido que se dio por WO va tachado (menos la celda del "WO", que
          // tiene que quedar legible): autotable no sabe tachar, así que la línea
          // se dibuja a mano sobre cada celda de la fila.
          didDrawCell: (data) => {
            if (data.section !== "body") return;
            if (hayWO && data.column.index === COLUMNA_WO) return;
            if (!esWO(grupo.partidos[data.row.index])) return;
            const medio = data.cell.y + data.cell.height / 2;
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.4);
            doc.line(data.cell.x, medio, data.cell.x + data.cell.width, medio);
          },
          didDrawPage: dibujarEncabezado,
        });

        y = doc.lastAutoTable.finalY + layout.sepGrupos;
      });
    });
  });

  const nombreArchivo = limpiarNombreArchivo(
    [
      "Planilla Arbitros",
      [torneoNombre || "", torneoAno].filter(Boolean).join(" "),
      numero ? "Fecha " + numero : "",
      dia || "",
    ]
      .filter(Boolean)
      .join(" - ")
  );

  doc.save(nombreArchivo + ".pdf");
}
